/**
 * Which heritage you are looking at.
 *
 * The site is no longer one country's museum with a language picker on top; it is a
 * place where several heritages are walked, and the region is the first choice a
 * visitor makes. Saudi Arabia leads because it is the only one with anything behind
 * it — the rest are named so the ambition is visible, and are deliberately not
 * clickable until they have exhibitions to open.
 *
 * Membership lives here rather than in `exhibitions/index.json` for exactly as long as
 * one region has content: a Pydantic field, a schema bump and a full republish buy
 * nothing while every published slug belongs to the same place. The moment a second
 * region opens, this moves into the registry and `regionOf` reads it from there.
 * `tests/regions.test.ts` reads the real registry and fails if a slug is unclaimed, so
 * the two cannot drift apart in the meantime.
 */

import { photographsIn } from "./collection";
import { scenesIn } from "./splat/scenes";

export type RegionId =
  | "saudi-arabia"
  | "united-arab-emirates"
  | "italy"
  | "greece"
  | "united-kingdom"
  | "ireland"
  | "france";

export type Region = {
  id: RegionId;
  /** Exhibition slugs from the registry. Empty means the region is not open yet. */
  slugs: readonly string[];
};

/** Display order, and the order the strip renders. What is finished comes first. */
export const REGIONS: readonly Region[] = [
  { id: "saudi-arabia", slugs: ["hegra", "turaif", "jubbah", "al-ahsa", "rijal-almaa"] },
  { id: "united-arab-emirates", slugs: [] },
  { id: "italy", slugs: [] },
  { id: "greece", slugs: [] },
  { id: "united-kingdom", slugs: [] },
  { id: "ireland", slugs: [] },
  { id: "france", slugs: [] },
];

export const DEFAULT_REGION: RegionId = "saudi-arabia";

/**
 * A region a visitor can actually enter.
 *
 * Exhibitions are not the only thing worth walking into. Greece has no built rooms at
 * all — it is photographs and the geometry generated from them — and it is nonetheless
 * the most complete thing on the site after Saudi Arabia. So "open" means "has
 * something", not "has exhibitions", or the strip would announce a heritage it then
 * refuses to show.
 */
export function isOpen(region: Region): boolean {
  return (
    region.slugs.length > 0 ||
    scenesIn(region.id).length > 0 ||
    photographsIn(region.id).length > 0
  );
}

export function openRegions(): readonly Region[] {
  return REGIONS.filter(isOpen);
}

export function findRegion(id: RegionId): Region {
  const region = REGIONS.find((candidate) => candidate.id === id);
  // Unreachable while RegionId and REGIONS are declared together, and cheaper to assert
  // than to thread a null through every caller.
  if (region === undefined) throw new Error(`unknown region: ${id}`);
  return region;
}

/** Narrow a query string or stored value to a region that is open. */
export function parseRegion(value: string | null): RegionId | null {
  const region = REGIONS.find((candidate) => candidate.id === value);
  return region !== undefined && isOpen(region) ? region.id : null;
}

/** Which region claims an exhibition, or null if nothing does. */
export function regionOf(slug: string): RegionId | null {
  return REGIONS.find((region) => region.slugs.includes(slug))?.id ?? null;
}
